import React, { useState, useEffect } from "react";
import {
  Modal,
  Transfer,
  Button,
  Typography,
  message,
  Spin,
  Tag,
  Divider,
} from "antd";
import { KeyOutlined, LockOutlined } from "@ant-design/icons";
import { assignPermissions } from "../../api/role_permissions/rolePermissionRequest";
import { getAllPermissions } from "../../api/permissions/permissionRequest";

const { Title, Text } = Typography;

const RolePermissionsModal = ({ visible, role, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [targetKeys, setTargetKeys] = useState([]);

  useEffect(() => {
    if (visible && role) {
      fetchData();
    }
  }, [visible, role]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Get all permissions
      const allPermissions = await getAllPermissions();

      // Transform permissions for Transfer component
      const formattedPermissions = allPermissions.map((perm) => ({
        key: perm.id.toString(),
        title: `${perm.resource}.${perm.action}`,
        description: `Permission to ${perm.action} ${perm.resource}`,
        resource: perm.resource,
        action: perm.action,
      }));

      setPermissions(formattedPermissions);

      // Set selected permissions if the role has any
      if (role.permissions && role.permissions.length > 0) {
        const selectedKeys = role.permissions.map((perm) => perm.id.toString());
        setTargetKeys(selectedKeys);
      } else {
        setTargetKeys([]);
      }
    } catch (error) {
      message.error("Failed to load permissions");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (newTargetKeys) => {
    setTargetKeys(newTargetKeys);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Convert string keys back to numbers
      const permissionIds = targetKeys.map((key) => parseInt(key));

      await assignPermissions(role.id, permissionIds);
      message.success("Permissions updated successfully");
      onClose();
    } catch (error) {
      message.error("Failed to update permissions");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Group permissions by resource for display
  const groupedPermissions = {};
  permissions.forEach((perm) => {
    if (!groupedPermissions[perm.resource]) {
      groupedPermissions[perm.resource] = [];
    }
    groupedPermissions[perm.resource].push(perm);
  });

  return (
    <Modal
      title={
        <div className="flex items-center">
          <LockOutlined className="text-xl mr-2 text-blue-600" />
          <span>
            Manage Permissions for{" "}
            <Text strong className="text-blue-700">
              {role?.name}
            </Text>
          </span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[]}
    >
      {loading && !permissions.length ? (
        <div className="flex justify-center my-12">
          <Spin size="large" />
        </div>
      ) : (
        <div>
          <div className="mb-4">
            <Text>
              Select the permissions you want to assign to this role. Move
              permissions from the left column to the right column.
            </Text>
          </div>
          <Transfer
            dataSource={permissions}
            titles={["Available Permissions", "Assigned Permissions"]}
            targetKeys={targetKeys}
            onChange={handleChange}
            render={(item) => (
              <div className="flex items-center">
                <KeyOutlined className="mr-2 text-gray-500" />
                <Tag
                  color={getColorByResource(item.resource)}
                  className="mr-2 py-1"
                >
                  {item.resource}
                </Tag>
                <span>{item.action}</span>
              </div>
            )}
            listStyle={{
              width: "100%",
              height: 400,
            }}
            showSearch
            filterOption={(inputValue, item) => {
              return (
                item.title.toLowerCase().indexOf(inputValue.toLowerCase()) >
                  -1 ||
                item.resource.toLowerCase().indexOf(inputValue.toLowerCase()) >
                  -1 ||
                item.action.toLowerCase().indexOf(inputValue.toLowerCase()) > -1
              );
            }}
          />
          <div className="flex justify-end mt-4 gap-2">

          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>
          
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleSubmit}
            className="bg-blue-600 hover:bg-blue-700"
            >
            Save Changes
          </Button>
            </div>
          <Divider />
          <div className="mt-4">
            <Title level={4}>Permission Groups</Title>
            <div className="grid grid-cols-2 gap-4 mt-2">
              {Object.keys(groupedPermissions).map((resource) => (
                <div
                  key={resource}
                  className="border rounded-lg p-3 bg-gray-50"
                >
                  <Text strong className="text-blue-700">
                    {resource}
                  </Text>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {groupedPermissions[resource].map((perm) => (
                      <Tag
                        key={perm.key}
                        color={
                          targetKeys.includes(perm.key) ? "blue" : "default"
                        }
                        className="px-2 py-1 text-xs"
                      >
                        {perm.action}
                      </Tag>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

// Helper function to assign colors based on resource name
const getColorByResource = (resource) => {
  const colors = {
    role: "blue",
    permission: "green",
    agent: "purple",
    payment: "orange",
    report: "cyan",
    infrastructure: "magenta",
    client: "gold",
  };

  return colors[resource] || "geekblue";
};

export default RolePermissionsModal;
